/**
 * La card del wizard collegata all'API. Compone i pezzi della lane E nello stesso impianto
 * della direzione 03 "Aria": fascia con il progresso in parole, domanda, perche lo chiedo,
 * pillole, campo libero con bozza, Invia, vie d'uscita, "Chiedi a mia …" (nome da display_form).
 *
 * Non usa il componente `WizardCard` gia composto perche qui servono tre cose che quello non
 * prevede: scelta multipla (WQ-B3-1, WQ-B6-F3, WQ-B10-4), card senza campo libero (WQ-B0-1) e
 * la riga di spiegazione quando la stessa card torna (dose senza unita). I pezzi sono gli stessi.
 */
import { useEffect, useState } from 'react'

import type { WizardAnswerKind } from '@mamma/contracts'
import {
  AnswerField,
  AskAgentLink,
  ChoiceChip,
  PrimaryButton,
  ProgressWords,
  QuestionTitle,
  SecondaryButton,
  WhyText,
  WizardFrame,
  readDraft,
} from '../components'
import type { WizardCardData } from '../api/wizard'
import { draftKeyFor } from './useWizard'

export type QuestionScreenProps = {
  card: WizardCardData
  recap?: string | null
  /** "Chiedi a mia <display_form>" (DEC-15): lo calcola chi conosce il nome, non la card. */
  askAgentLabel: string
  busy?: boolean
  onAnswer: (scelte: string[], testo: string) => void
  onEscape: (kind: WizardAnswerKind) => void
  onAskAgent: () => void
  onPause: () => void
}

export function QuestionScreen({
  card,
  recap,
  askAgentLabel,
  busy = false,
  onAnswer,
  onEscape,
  onAskAgent,
  onPause,
}: QuestionScreenProps) {
  const draftKey = draftKeyFor(card)
  const [scelte, setScelte] = useState<string[]>([])
  const [testo, setTesto] = useState(() => readDraft(draftKey))

  // Card nuova: nessuna scelta ereditata, e il testo e quello della sua bozza.
  useEffect(() => {
    setScelte([])
    setTesto(readDraft(draftKey))
  }, [draftKey])

  const scegli = (id: string) => {
    setScelte((precedenti) => {
      if (!card.multi) return precedenti[0] === id ? [] : [id]
      return precedenti.includes(id) ? precedenti.filter((x) => x !== id) : [...precedenti, id]
    })
  }

  const puoInviare = scelte.length > 0 || testo.trim().length > 0

  return (
    <WizardFrame band={<ProgressWords block={card.block_title_it} progress={card.progress_it} />}>
      {recap && (
        <p className="text-soft mb-3 text-meta text-ink" data-testid="recap">
          {recap}
        </p>
      )}
      <QuestionTitle>{card.question_it}</QuestionTitle>
      <div className="mb-auto">
        <WhyText>{card.purpose_it}</WhyText>
        {card.note_it && (
          <p className="mt-2 text-why text-ink" data-testid="nota">
            {card.note_it}
          </p>
        )}
      </div>

      {card.options.length > 0 && (
        <div className="mt-[6px] flex flex-wrap gap-x-2 gap-y-[10px]">
          {card.options.map((opzione) => (
            <ChoiceChip
              key={opzione.id}
              label={opzione.label_it}
              selected={scelte.includes(opzione.id)}
              disabled={busy}
              onSelect={() => scegli(opzione.id)}
            />
          ))}
        </div>
      )}

      {card.free_text && (
        <AnswerField
          label={card.field_label_it ?? 'Scriva qui'}
          draftKey={draftKey}
          disabled={busy}
          onChange={setTesto}
        />
      )}

      <PrimaryButton disabled={busy || !puoInviare} onClick={() => onAnswer(scelte, testo)}>
        Invia
      </PrimaryButton>

      <div className="mt-3 flex flex-wrap gap-2.5">
        {card.escapes.map((via) => (
          <SecondaryButton key={via.kind} grow disabled={busy} onClick={() => onEscape(via.kind)}>
            {via.label_it}
          </SecondaryButton>
        ))}
      </div>

      <div className="mt-3">
        <SecondaryButton disabled={busy} onClick={onPause}>
          Riprendo un&apos;altra volta
        </SecondaryButton>
      </div>

      <AskAgentLink label={askAgentLabel} onAsk={onAskAgent} />
    </WizardFrame>
  )
}
